<template>
    <div class="congress-schedule-event-card">
        <!-- Collapsible Header -->
        <div class="congress-schedule-event-card-header" @click="toggleExpanded">
            <div class="congress-schedule-event-card-header-inner">
                <div class="congress-schedule-event-card-header-inner-left">
                    <icon
                        icon="phosphor/regular/arrows-out-cardinal"
                        class="congress-schedule-event-card-header-move cursor-grab"
                    ></icon>
                </div>
                <div class="congress-schedule-event-card-header-label">
                    <span class="font-medium">
                        {{ member.name || lang('congresses::congresses.committee_members.untitled') }}
                    </span>
                    <span v-if="member.role" class="ml-2 text-gray-500">
                        ({{ member.role }})
                    </span>
                </div>
                <div class="congress-schedule-event-card-header-actions">
                    <Button type="button" size="sm" color="danger" variant="inline" @click.stop="$emit('remove')">
                        <span class="action-icon">
                            <Icon icon="phosphor/regular/trash"></Icon>
                        </span>
                        <span class="action-label">
                            {{ lang('congresses::congresses.committee_members.remove') }}
                        </span>
                    </Button>
                    <icon
                        icon="hero/outline/chevron-down"
                        class="congress-schedule-event-card-header-actions-icon"
                        :class="{ 'rotate-180': expanded }"
                    ></icon>
                </div>
            </div>
        </div>

        <!-- Expanded Content -->
        <div v-show="expanded" class="event-content mt-2 space-y-2 px-5">
            <div class="grid grid-cols-2 gap-5">
                <Field
                    v-model="member.name"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.committee_members.name'),
                        slug: `committees.${committeeIndex}.members.${index}.name`,
                        type: 'text',
                        adminComponent: 'AdminText',
                        required: true,
                    }"
                />

                <Field
                    v-model="member.role"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.committee_members.role'),
                        slug: `committees.${committeeIndex}.members.${index}.role`,
                        type: 'text',
                        adminComponent: 'AdminText',
                    }"
                />
            </div>

            <div class="grid grid-cols-2 gap-5">
                <Field
                    v-model="member.email"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.committee_members.email'),
                        slug: `committees.${committeeIndex}.members.${index}.email`,
                        type: 'email',
                        adminComponent: 'AdminEmail',
                    }"
                />

                <Field
                    v-model="member.phone"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.committee_members.phone'),
                        slug: `committees.${committeeIndex}.members.${index}.phone`,
                        type: 'tel',
                        adminComponent: 'AdminTel',
                    }"
                />
            </div>

            <Field
                v-model="member.bio"
                label-position="top"
                :field="{
                    name: lang('congresses::congresses.committee_members.bio'),
                    slug: `committees.${committeeIndex}.members.${index}.bio`,
                    type: 'wysiwyg',
                    adminComponent: 'AdminWYSIWYG',
                    options: {
                        text: ['normal', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
                        fonts: true,
                        font_sizes: true,
                    },
                }"
            />

            <div class="grid grid-cols-3 gap-5">
                <Field
                    v-model="member.avatar_media_id"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.committee_members.avatar'),
                        slug: `committees.${committeeIndex}.members.${index}.avatar_media_id`,
                        type: 'file',
                        adminComponent: 'AdminFile',
                    }"
                />

                <Field
                    v-model="member.color"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.committee_members.color'),
                        slug: `committees.${committeeIndex}.members.${index}.color`,
                        type: 'select',
                        adminComponent: 'AdminSelect',
                        portal: true,
                        options: colorOptions,
                    }"
                />

                <Field
                    v-model="member.display_style"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.committee_members.display_style'),
                        slug: `committees.${committeeIndex}.members.${index}.display_style`,
                        type: 'select',
                        adminComponent: 'AdminSelect',
                        portal: true,
                        options: displayStyleOptions,
                    }"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { lang } from '@/lang/composable';
import Button from '@src/Common/Components/Button.vue';
import Field from '@src/Common/Components/Field.vue';
import type { CommitteeMember } from '../types';

defineProps<{
    index: number;
    committeeIndex: number;
}>();

defineEmits<{
    (e: 'remove'): void;
}>();

const member = defineModel<CommitteeMember>();

const expanded = ref(false);

const toggleExpanded = () => {
    expanded.value = !expanded.value;
};

const colorOptions = [
    { id: '', name: lang('congresses::congresses.committee_members.color_none') },
    { id: 'red', name: lang('congresses::congresses.committee_members.color_red') },
    { id: 'gold', name: lang('congresses::congresses.committee_members.color_gold') },
    { id: 'blue', name: lang('congresses::congresses.committee_members.color_blue') },
];

const displayStyleOptions = [
    { id: '', name: lang('congresses::congresses.committee_members.display_style_inherit') },
    { id: 'default', name: lang('congresses::congresses.committee_members.display_style_default') },
    { id: 'compact', name: lang('congresses::congresses.committee_members.display_style_compact') },
];
</script>
